const API_KEY = process.env.REACT_APP_API_KEY;

// Simple in-memory GET cache with TTL
const cache = new Map();
const CACHE_TTL = 30000; // 30 seconds

const api = {
  fetch: async (url, options = {}) => {
    // Use the correct token based on the endpoint
    const isOnlinePlayer = url.includes('/onlineplayer');
    const token = isOnlinePlayer
      ? localStorage.getItem('bc_viewer_token')
      : (localStorage.getItem('token') || localStorage.getItem('bc_viewer_token'));

    const headers = {
      'x-api-key': '8f6963a7b5a69e2ec18df35a488c356438aaf5e2a7bc71dae66ca8a9089df537',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };

    // Only set Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      // The body is not always JSON — an edge proxy 502/504 returns HTML, and
      // response.json() would throw a SyntaxError that masks the real status.
      let body;
      try {
        body = await response.json();
      } catch (_) {
        body = { message: `Server error (${response.status})` };
      }

      // Auto-logout on token expiration for online player
      if (response.status === 401 && localStorage.getItem('bc_viewer_token')) {
        localStorage.removeItem('bc_viewer_token');
        localStorage.removeItem('bc_viewer_data');
        window.location.reload();
      }

      const error = new Error(body.error || body.message || 'API request failed');
      // Callers were written against axios and read error.response.status /
      // error.response.data.message. Without these the whole catch block in
      // Dashboard.jsx is dead code and start failures are invisible.
      error.status = response.status;
      error.response = { status: response.status, data: body };
      throw error;
    }

    return response.json();
  },

  get: (url, options = {}) => {
    // Check cache for GET requests
    const cached = cache.get(url);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return Promise.resolve(cached.data);
    }

    return api.fetch(url, { method: 'GET', ...options }).then(data => {
      cache.set(url, { data, timestamp: Date.now() });
      return data;
    });
  },

  post: (url, data, options = {}) => {
    // Invalidate cache for this URL on mutation
    cache.delete(url);

    let body;

    if (data instanceof FormData) {
      body = data;
    } else {
      body = JSON.stringify(data);
    }

    return api.fetch(url, {
      method: 'POST',
      body,
      ...options,
    });
  },

  put: (url, data) => {
    cache.delete(url);
    return api.fetch(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: (url, options = {}) => {
    cache.delete(url);
    return api.fetch(url, { method: 'DELETE', ...options });
  },

  // Clear all cached data (useful after login/logout)
  clearCache: () => cache.clear(),
};

const handleApiError = (error) => {
    if (error.message.includes('IP is blocked')) {
      window.location.href = '/blocked';
    } else if (error.message.includes('Unauthorized')) {
      window.location.href = '/login';
    } else {
      console.error('API Error:', error);
    }
  };

// ===== ONLINE PLAYER API =====

const OP_API = () => {
  const base = process.env.REACT_APP_API || 'http://localhost:5005/api';
  return `${base}/onlineplayer`;
};

const onlinePlayerApi = {
  login: (phone, password) =>
    api.post(`${OP_API()}/login`, { phone, password }),

  register: (phone, password, shopCode) =>
    api.post(`${OP_API()}/register`, { phone, password, shopCode: shopCode || undefined }),

  getMe: () => api.get(`${OP_API()}/me`),

  changePassword: (currentPassword, newPassword) =>
    api.post(`${OP_API()}/change-password`, { currentPassword, newPassword }),

  getWallet: () => api.get(`${OP_API()}/wallet`),

  getTransactions: (limit = 20, skip = 0) =>
    api.get(`${OP_API()}/transactions?limit=${limit}&skip=${skip}`),

  requestDeposit: (amount, screenshot, screenshotMimeType, paymentMethod, senderDetails) =>
    api.post(`${OP_API()}/deposits`, { amount, screenshot, screenshotMimeType, paymentMethod, senderDetails }),

  getPaymentMethods: () => api.get(`${OP_API()}/payment-methods`),

  getMyDeposits: (limit = 20, skip = 0, status) =>
    api.get(`${OP_API()}/deposits?limit=${limit}&skip=${skip}${status ? '&status=' + status : ''}`),

  getCurrentSession: () => api.get(`${OP_API()}/current-session`),

  getMyCards: () => api.get(`${OP_API()}/my-cards`),

  claimCard: (cardNumber, sessionId) =>
    api.post(`${OP_API()}/claim-card`, { cardNumber, sessionId }),

  releaseCard: (cardNumber, sessionId) =>
    api.post(`${OP_API()}/release-card`, { cardNumber, sessionId }),

  requestWithdrawal: (amount) =>
    api.post(`${OP_API()}/withdraw`, { amount }),

  setShopCode: (shopCode) =>
    api.post(`${OP_API()}/set-shop-code`, { shopCode }),
};

export { onlinePlayerApi };
export default api;
